import React from 'react';
import { scoreColor, num, pct, metricsFromProfile, APT } from './aptitudeHelpers';
import { BEHAVIOUR_META } from '../diagnostic/diagnosticHelpers';

// Overview tab — the CURRENT rolling profile (aptitude_profile, Tier 2).
// Distinct look from the Diagnostic report: violet lead card + compact behaviour
// chips + a sparkline of the metric's own history. Uses stored values only.

const Spark = ({ series, color }) => {
  const vals = (series || []).map(num).filter((v) => v != null);
  if (vals.length < 2) return null;
  const max = Math.max(...vals, 1);
  return (
    <div className="spark">
      {vals.slice(-12).map((v, i) => (
        <div key={i} className="apt-spark-bar" style={{ height: `${Math.max((v / max) * 100, 6)}%`, background: color }} />
      ))}
    </div>
  );
};

const MetricCard = ({ lead, icon, label, value, desc, series, color }) => (
  <div className={`apt-metric-card ${lead ? 'lead' : ''}`}>
    <div className="cap"><span>{icon}</span>{label}</div>
    <div className="big" style={{ color }}>{value}</div>
    <p className="desc">{desc}</p>
    <Spark series={series} color={color} />
  </div>
);

const AptitudeOverview = ({ profile, trend }) => {
  if (!profile) {
    return (
      <div className="apt-empty">
        <span className="i">📉</span>
        <h3 style={{ margin: '0 0 6px', color: '#0f172a' }}>No aptitude data yet</h3>
        <p style={{ margin: 0 }}>Complete an aptitude test to start building the continuous performance profile.</p>
      </div>
    );
  }

  const kSeries = (trend || []).map((t) => t.knowledge);
  const bSeries = (trend || []).map((t) => t.behaviour);
  const eSeries = (trend || []).map((t) => t.eri);

  const know = num(profile.current_knowledge);
  const beh = num(profile.current_behaviour);
  const eri = num(profile.current_eri);

  const metrics = metricsFromProfile(profile);
  const topics = profile.topic_mastery || [];

  return (
    <>
      <section className="apt-section">
        <div className="apt-metric-grid">
          <MetricCard lead icon="🧠" label="Knowledge" value={pct(know)} color={scoreColor(know)}
            desc="Rolling academic performance across all aptitude attempts." series={kSeries} />
          <MetricCard icon="🎯" label="Behaviour" value={pct(beh)} color={scoreColor(beh)}
            desc="Composite of TPSI, CER, RCI and FFI over time." series={bSeries} />
          <MetricCard icon="⚡" label="Employability Readiness (ERI)" value={pct(eri)} color={scoreColor(eri)}
            desc="Guidance indicator blending performance and behaviour." series={eSeries} />
        </div>
      </section>

      {metrics && (
        <section className="apt-section">
          <div className="apt-section-head">
            <h3 className="apt-section-title">Behavioural Signals</h3>
            <p className="apt-section-desc">Current rolling values across your attempts</p>
          </div>
          <div className="apt-chip-grid">
            {['TPSI', 'CER', 'RCI', 'FFI'].map((code) => {
              const v = num(metrics[code]?.score);
              const c = v == null ? '#94a3b8' : scoreColor(v);
              return (
                <div className="apt-chip" key={code}>
                  <div className="code">{code}</div>
                  <div className="val" style={{ color: c }}>{v == null ? 'N/A' : Math.round(v)}</div>
                  <div className="name">{BEHAVIOUR_META[code]?.label}</div>
                  <div className="track"><div className="fill" style={{ width: `${v || 0}%`, background: c }} /></div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {topics.length > 0 && (
        <section className="apt-section">
          <div className="apt-section-head">
            <h3 className="apt-section-title">Topic Mastery</h3>
            <p className="apt-section-desc">Rolling accuracy per topic, strongest first</p>
          </div>
          <div className="apt-card">
            {[...topics].sort((a, b) => (num(b.accuracy) || 0) - (num(a.accuracy) || 0)).map((t, i) => {
              const acc = num(t.accuracy) || 0;
              const c = scoreColor(acc);
              return (
                <div className="apt-topic-row" key={`${t.topic_name}-${i}`}>
                  <div>
                    <div className="apt-topic-name">{t.topic_name}</div>
                    {t.category && <div className="apt-topic-cat">{t.category}</div>}
                  </div>
                  <div className="apt-topic-track"><div className="apt-topic-fill" style={{ width: `${acc}%`, background: c }} /></div>
                  <div className="apt-topic-val" style={{ color: c }}>{Math.round(acc)}%</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {(profile.strong_areas?.length > 0 || profile.weak_areas?.length > 0) && (
        <section className="apt-section">
          <div className="apt-cmp-lists">
            <div className="apt-card">
              <h3 className="apt-section-title" style={{ marginBottom: 12 }}>💪 Consistent Strengths</h3>
              {profile.strong_areas?.length ? (
                <ul className="apt-diff-list">
                  {profile.strong_areas.map((s, i) => <li className="added" key={i}><span className="apt-diff-tag" style={{ background: 'rgba(5,150,105,0.15)', color: APT.up }}>✓</span>{typeof s === 'string' ? s : s.topic_name}</li>)}
                </ul>
              ) : <p className="apt-muted">Keep going to surface consistent strengths.</p>}
            </div>
            <div className="apt-card">
              <h3 className="apt-section-title" style={{ marginBottom: 12 }}>🎯 Focus Areas</h3>
              {profile.weak_areas?.length ? (
                <ul className="apt-diff-list">
                  {profile.weak_areas.map((s, i) => <li className="removed" key={i}><span className="apt-diff-tag" style={{ background: 'rgba(225,29,72,0.14)', color: APT.down }}>!</span>{typeof s === 'string' ? s : s.topic_name}</li>)}
                </ul>
              ) : <p className="apt-muted">No critical focus areas right now — nice work.</p>}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default AptitudeOverview;
